export const CAMPAIGN_QUESTION_TYPES = [
	'SHORT_TEXT',
	'LONG_TEXT',
	'BOOLEAN',
	'SINGLE_CHOICE',
	'MULTIPLE_CHOICE',
	'SCALE',
	'FILE',
] as const

export type CampaignQuestionType = (typeof CAMPAIGN_QUESTION_TYPES)[number]

export type CampaignStatus = 'DRAFT' | 'PUBLISHED' | 'CLOSED'

export type CampaignAvailability = 'DRAFT' | 'SCHEDULED' | 'OPEN' | 'ENDED'

export type CampaignConditionOperator = 'EQUALS' | 'NOT_EQUALS'

export interface CampaignQuestion {
	id: string
	type: CampaignQuestionType
	label: string
	helpText: string | null
	required: boolean
	position: number
	options: string[]
	scaleMin: number | null
	scaleMax: number | null
	condition: {
		questionId: string
		operator: CampaignConditionOperator
		value: string
	} | null
}

export interface CampaignReader {
	id: string
	title: string
	description: string | null
	startsAt: string | null
	endsAt: string | null
	allowMultiple: boolean
	availability: CampaignAvailability
	myResponses: number
	canRespond: boolean
	questions: CampaignQuestion[]
}

export interface CampaignReaderListItem {
	id: string
	title: string
	description: string | null
	endsAt: string | null
	questionCount: number
	myResponses: number
	allowMultiple: boolean
}

export interface CampaignAdmin {
	id: string
	title: string
	description: string | null
	status: CampaignStatus
	availability: CampaignAvailability
	startsAt: string | null
	endsAt: string | null
	allowMultiple: boolean
	isGlobal: boolean
	branchIds: string[]
	responseCount: number
	createdAt: string
	questions: CampaignQuestion[]
}

export interface CampaignAdminList {
	items: CampaignAdmin[]
	pagination: { page: number; limit: number; total: number; totalPages: number }
}

/** Pergunta como o formulário de criação a edita; `key` liga as condições antes de existir id */
export interface CampaignQuestionDraft {
	key: string
	type: CampaignQuestionType
	label: string
	helpText: string
	required: boolean
	options: string[]
	scaleMin: number
	scaleMax: number
	condition: {
		questionKey: string
		operator: CampaignConditionOperator
		value: string
	} | null
}

export interface CampaignInput {
	title: string
	description: string | null
	startsAt: string | null
	endsAt: string | null
	allowMultiple: boolean
	isGlobal: boolean
	branchIds: string[]
	questions?: CampaignQuestionDraft[]
}

/** Resposta de uma pergunta no formulário de quem responde */
export interface CampaignAnswerDraft {
	text?: string
	number?: number
	boolean?: boolean
	choices?: string[]
	file?: File
}

export interface CampaignAnswerOutput {
	questionId: string
	question: string
	type: CampaignQuestionType
	textValue: string | null
	numberValue: number | null
	booleanValue: boolean | null
	choiceValues: string[]
	fileName: string | null
	fileUrl: string | null
}

export interface CampaignResponseOutput {
	id: string
	submittedAt: string
	personName: string
	badge: string
	jobRole: string | null
	branchName: string
	crName: string
	answers: CampaignAnswerOutput[]
}

export interface CampaignResponseList {
	items: CampaignResponseOutput[]
	pagination: { page: number; limit: number; total: number; totalPages: number }
}

export interface CampaignQuestionResult {
	questionId: string
	label: string
	type: CampaignQuestionType
	answered: number
	choices?: { value: string; count: number; pct: number }[]
	boolean?: { yes: number; no: number }
	scale?: { average: number; distribution: { value: number; count: number }[] }
	texts?: { text: string; personName: string; date: string }[]
	files?: number
}

export interface CampaignPersonRow {
	personId: string
	name: string
	badge: string
	jobRole: string | null
	branchName: string
	crName: string
	responses: number
	lastResponseAt: string | null
	responded: boolean
}

export interface CampaignResults {
	totals: {
		peopleInScope: number
		respondents: number
		responses: number
		participationPct: number
	}
	timeline: { date: string; responses: number }[]
	byBranch: {
		branchId: string
		branchName: string
		people: number
		respondents: number
		participationPct: number
	}[]
	byJobRole: {
		jobRole: string
		people: number
		respondents: number
		participationPct: number
	}[]
	questions: CampaignQuestionResult[]
	jobRoles: string[]
	branches: { id: string; name: string }[]
	peopleSummary: { total: number; responded: number }
	people: CampaignPersonRow[]
	peopleTruncated: boolean
}

export interface CampaignResultsParams {
	startDate?: string
	endDate?: string
	personStatus?: 'responded' | 'notResponded'
	personJobRole?: string
	personBranchId?: string
}
